import { useEffect, useMemo, type ReactNode } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useQuery } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { FileText, Share2, SquarePen } from 'lucide-react'
import { toast } from 'sonner'

import { cn } from '@/lib/utils'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { useHasJobsFeature } from '@/hooks/use-has-feature'
import { usePortalLinkCopy } from '@/hooks/use-portal-link-copy'
import { useGeneratePortalLink } from '@/stores/leads.store'
import { useInvalidateJobAfterQuoteChange } from '@/stores/pipeline.store'
import {
  getJob,
  listJobEvents,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  GetJobRequestSchema,
  ListJobEventsRequestSchema,
} from '@/gen/cortado/v1/jobs_pb'
import { GeneratePortalLinkRequestSchema } from '@/gen/cortado/v1/inquiries_pb'
import { InquiryStageEnum, JobPhaseEnum, JobStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { formatEnumName } from '@/lib/enum-utils'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Empty, EmptyDescription, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import {
  PIPELINE_PHASE_PARAMS,
  PIPELINE_PHASE_STEPS,
  pipelineLayoutPhase,
  pipelinePhaseParam,
  pipelinePhaseSegments,
  pipelinePhaseStep,
  pipelinePhaseStepFromParam,
  type PipelinePhaseStep,
} from '@/components/pipeline/pipeline-job-view'
import { JobPhaseViewBanner } from '@/components/pipeline/job-phase-view-banner'
import { PipelineStageCell } from '@/components/pipeline/pipeline-stage-cell'
import { PipelineLeadStageControl } from '@/components/pipeline/pipeline-lead-stage-control'
import { ReceivedTimeLabel } from '@/components/pipeline/received-time-label'
import { JobOriginalInquiry } from '@/components/pipeline/job-original-inquiry'
import { JobEvents } from '@/components/pipeline/job-events'
import { JobQuotes } from '@/components/pipeline/job-quotes'
import { NEW_QUOTE_ID } from '@/components/pipeline/pipeline-quote-route'
import { NewQuoteMenu } from '@/components/pipeline/new-quote-menu'
import { JobAssignee } from '@/components/pipeline/job-assignee'
import { LeadReferralStatus } from '@/components/leads/referrals/lead-referral-status'
import { JobActionsMenu } from '@/components/pipeline/job-actions-menu'
import { JobContactHeader } from '@/components/pipeline/job-contact-header'
import { InquiryTagsEditor } from '@/components/leads/inquiry-tags-editor'
import { EventTypeBadge } from '@/components/shared/enum-badge'
import { LeadOverlapStrip } from '@/components/leads/id/lead-overlap-banner'
import { useInquiryOverlaps } from '@/stores/overlaps.store'
import { useQuoteLitesByIds } from '@/stores/quotes.store'
import { JobActivityCard } from '@/components/pipeline/job-activity-card'
import { JobRail } from '@/components/pipeline/job-rail'
import { JobBooking } from '@/components/pipeline/job-booking'
import { JobLogisticsCard, type LogisticsOpenCheck } from '@/components/pipeline/job-logistics-card'
import { LOGISTICS_CHECK_ORDER, type LogisticsCheckKey } from '@/components/pipeline/job-logistics'
import { jobBreadcrumbs } from '@/components/pipeline/pipeline-breadcrumbs'
import { JobWrapUp } from '@/components/pipeline/job-wrap-up'
import { JobBookingCta } from '@/components/pipeline/job-booking-cta'
import { RejectReasonBanner } from '@/components/pipeline/reject-reason-banner'
import { useInvoiceLitesByIds } from '@/stores/invoices.store'
import { useInquiry } from '@/stores/leads.store'
import {
  jobEarliestEventDay,
  jobCurrentQuote,
  jobLatestDraftQuote,
  jobPrimaryQuoteAction,
  jobDetailHasQuote,
  jobDetailLeadStageTitle,
  jobDetailQuoteStageTitle,
  jobDetailStatusVariant,
  jobBookingTotals,
  jobDetailBookingStageTitle,
  jobNextBookedEvent,
  jobBookingPrimaryAction,
  jobBookedEvents,
} from '@/components/pipeline/job-detail-view'

const NO_IDS: string[] = []

export const Route = createFileRoute('/_app/_home/pipeline/$id')({
  component: PipelineJobRouteFromParams,
  // The strip step the content is laid out for; a bad value falls back to the job's own step.
  validateSearch: z.object({
    phase: z.enum(PIPELINE_PHASE_PARAMS).optional().catch(undefined),
    // The Logistics step open in a sheet: the event it is for and the step.
    event: z.string().optional().catch(undefined),
    logistics: z
      .enum(LOGISTICS_CHECK_ORDER as [LogisticsCheckKey, ...LogisticsCheckKey[]])
      .optional()
      .catch(undefined),
  }),
})

function PipelineJobRouteFromParams() {
  const { id } = Route.useParams()
  const { phase, event, logistics } = Route.useSearch()
  return (
    <PipelineJobRoute
      jobId={id}
      viewStep={pipelinePhaseStepFromParam(phase)}
      openEventId={event}
      openLogistics={logistics}
    />
  )
}

interface PipelineJobRouteProps {
  jobId: string
  viewStep?: PipelinePhaseStep
  openEventId?: string
  openLogistics?: LogisticsCheckKey
}

export function PipelineJobRoute({ jobId, viewStep, openEventId, openLogistics }: PipelineJobRouteProps) {
  const navigate = useNavigate()
  const { hasFeature, hasLoaded } = useHasJobsFeature()

  useEffect(() => {
    if (hasLoaded && !hasFeature) {
      navigate({ to: '/dashboard', replace: true })
    }
  }, [hasFeature, hasLoaded, navigate])

  if (!hasLoaded || !hasFeature) {
    return <div className="flex min-h-[50dvh] items-center justify-center">Loading...</div>
  }

  return (
    <PipelineJobPage jobId={jobId} viewStep={viewStep} openEventId={openEventId} openLogistics={openLogistics} />
  )
}

export function PipelineJobPage({
  jobId,
  viewStep: requestedViewStep,
  openEventId,
  openLogistics,
}: PipelineJobRouteProps) {
  const navigate = useNavigate()
  const invalidateJob = useInvalidateJobAfterQuoteChange()

  const jobRequest = useMemo(() => create(GetJobRequestSchema, { jobId }), [jobId])
  const eventsRequest = useMemo(() => create(ListJobEventsRequestSchema, { jobId }), [jobId])

  const jobQuery = useQuery(getJob, jobRequest, { enabled: Boolean(jobId) })
  const eventsQuery = useQuery(listJobEvents, eventsRequest, { enabled: Boolean(jobId) })
  const loadedJob = jobQuery.data?.job
  const breadcrumbs = useMemo(() => (loadedJob ? jobBreadcrumbs(loadedJob) : undefined), [loadedJob])
  useBreadcrumbFromRoute(breadcrumbs)

  const { generateLink } = useGeneratePortalLink()
  const inquiryId = jobQuery.data?.job?.inquiryId
  // Shares its `useInquiry` query key with `PipelineLeadStageControl` below
  // and the "Original Inquiry" card, so this doesn't add extra inquiry fetches.
  const { inquiry, isLoading: isInquiryLoading } = useInquiry(inquiryId)
  // Read by both the Booking card and the Booking stepper cell.
  const invoicesQuery = useInvoiceLitesByIds(jobQuery.data?.job?.invoiceIds ?? NO_IDS)
  // Shares its query with the Quotes card and the New quote menu. Drives the
  // Lead step's title, the header's primary quote action and the draft links.
  const quoteLites = useQuoteLitesByIds(jobQuery.data?.job?.quoteIds ?? NO_IDS)
  // Shares its query with the Events card's date-tile warnings.
  const overlaps = useInquiryOverlaps(inquiryId)
  const { copyGenericLink, isCopying: isCopyingPortalLink } = usePortalLinkCopy(
    (attributionMode) =>
      generateLink(create(GeneratePortalLinkRequestSchema, { inquiryId: inquiryId ?? '', attributionMode })),
    (error) => {
      reportError(error, { feature: 'portal_link_generate', kind: 'mutation' })
      toast.error('Failed to generate portal link', { description: getErrorDescription(error) })
    }
  )

  if (jobQuery.isLoading) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-24 w-full rounded-xl" />
        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <Skeleton className="h-64 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    )
  }

  const job = jobQuery.data?.job

  if (jobQuery.isError || !job) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <div className="flex flex-1 flex-col rounded-lg border border-dashed py-6 md:py-0">
          <Empty>
            <EmptyMedia variant="icon">
              <FileText className="h-6 w-6" />
            </EmptyMedia>
            <EmptyTitle>Job not found</EmptyTitle>
            <EmptyDescription>We couldn&apos;t load this job. It may have been removed.</EmptyDescription>
          </Empty>
        </div>
      </div>
    )
  }

  function draftQuote() {
    navigate({ to: '/pipeline/$id/quotes/$quoteId', params: { id: jobId, quoteId: NEW_QUOTE_ID } })
  }

  const events = eventsQuery.data?.events ?? []
  const phaseSegments = pipelinePhaseSegments(job.phase)
  // The strip's reached steps lay the content out as that step did; header
  // actions keep following the job's real phase.
  const liveStep = pipelinePhaseStep(job.phase)
  // From the URL; a step the job hasn't reached has nothing to show, so it's ignored.
  const viewStep =
    requestedViewStep && phaseSegments[PIPELINE_PHASE_STEPS.indexOf(requestedViewStep)] !== 'future'
      ? requestedViewStep
      : undefined
  const viewPhase = (step: PipelinePhaseStep | undefined) =>
    navigate({
      to: '/pipeline/$id',
      params: { id: jobId },
      search: { phase: step && step !== liveStep ? pipelinePhaseParam(step) : undefined },
      replace: true,
    })
  // Only a step for one of the job's booked events opens; a stale or foreign id opens nothing.
  const openCheck: LogisticsOpenCheck | undefined =
    openLogistics && openEventId && jobBookedEvents(events).some((event) => event.id === openEventId)
      ? { eventId: openEventId, key: openLogistics }
      : undefined
  const openLogisticsCheck = (next: LogisticsOpenCheck | undefined) =>
    navigate({
      to: '/pipeline/$id',
      params: { id: jobId },
      search: (prev) => ({ ...prev, event: next?.eventId, logistics: next?.key }),
      replace: true,
    })
  const layoutPhase = pipelineLayoutPhase(job.phase, viewStep)
  const isViewingOtherPhase = layoutPhase !== job.phase
  const canViewPhases = phaseSegments.filter((state) => state !== 'future').length > 1
  // Once the job is past the lead phase the request is settled context: quotes
  // (then the booking) are the working surface, so the request cards start
  // collapsed. A booked job's Events card lists the booked events, which stay
  // open; a complete job's events are behind it, so Quoting and Complete
  // collapse that one and Complete leads with the Wrap-up card.
  const isComplete = layoutPhase === JobPhaseEnum.JOB_PHASE_COMPLETE
  const isRequestCollapsed =
    layoutPhase === JobPhaseEnum.JOB_PHASE_QUOTING || layoutPhase === JobPhaseEnum.JOB_PHASE_BOOKED || isComplete
  const isEventsCollapsed = layoutPhase === JobPhaseEnum.JOB_PHASE_QUOTING || isComplete
  const isBookedOrComplete = layoutPhase === JobPhaseEnum.JOB_PHASE_BOOKED || isComplete
  // Undefined while the booking's invoices load, so the cell doesn't flash "Not invoiced".
  const bookingInvoices =
    job.bookingId && (invoicesQuery.hasLoaded || job.invoiceIds.length === 0) ? invoicesQuery.invoices : undefined
  const bookingTotals = bookingInvoices ? jobBookingTotals(bookingInvoices) : undefined
  const nextBookedEvent = jobNextBookedEvent(events)
  // Booked and Complete: the header's primary action follows the Booking step's sub-state.
  const bookingAction = jobBookingPrimaryAction(job, bookingInvoices, nextBookedEvent)
  // A lost job says why, with the reason editable, under the header.
  const isDeclined =
    job.status === JobStatusEnum.JOB_STATUS_LOST && inquiry?.stage === InquiryStageEnum.INQUIRY_STAGE_REJECTED
  // Undefined while the quotes load, so nothing derived from them flips mid-load.
  const loadedQuotes = job.quoteIds.length === 0 || quoteLites.hasLoaded ? quoteLites.quotes : undefined
  const quoteStatuses = loadedQuotes?.map((quote) => quote.status)
  const isLeadPhase = job.phase === JobPhaseEnum.JOB_PHASE_NEW
  const leadDraftQuote = isLeadPhase && loadedQuotes ? jobLatestDraftQuote(loadedQuotes) : undefined
  const primaryQuoteAction = jobPrimaryQuoteAction(job, loadedQuotes)
  // Quoting: the requested events read first and point at this quote for client changes.
  const isQuotingPhase = layoutPhase === JobPhaseEnum.JOB_PHASE_QUOTING
  const currentQuote = isQuotingPhase ? jobCurrentQuote(job, loadedQuotes) : undefined
  // Conflicts matter until the job is booked; a booked job's events are its schedule.
  const showConflicts =
    Boolean(job.inquiryId) && (layoutPhase === JobPhaseEnum.JOB_PHASE_NEW || isQuotingPhase)

  // One short status per step so all three fit on a line; only Lead adds when it came in.
  const stageCells: { label: PipelinePhaseStep; title: ReactNode; subtitle?: ReactNode }[] = [
    {
      label: 'Lead',
      title: jobDetailLeadStageTitle(job, { inquiryStage: inquiry?.stage, quoteStatuses }),
      subtitle: <ReceivedTimeLabel createdAt={inquiry?.createdAt ?? job.createdAt} className="font-sans" />,
    },
    { label: 'Quote', title: jobDetailQuoteStageTitle(job, loadedQuotes) },
    { label: 'Booking', title: jobDetailBookingStageTitle(job, bookingTotals) },
  ]

  const hasStatus = job.status !== JobStatusEnum.JOB_STATUS_UNSPECIFIED
  // A lost job shows its declined banner instead of the Booking card.
  const showBookingCard =
    Boolean(job.bookingId) && isBookedOrComplete && job.status !== JobStatusEnum.JOB_STATUS_LOST
  const showLogistics = showBookingCard && job.phase === JobPhaseEnum.JOB_PHASE_BOOKED

  // The Quotes card's "+ New quote" — the job's one way to start a quote.
  const newQuoteMenu = (
    <NewQuoteMenu
      variant="ghost"
      jobId={job.id}
      organizationId={inquiry?.organizationId || job.organizationId}
      eventTypeKey={inquiry?.eventTypeKey || undefined}
      quoteIds={job.quoteIds}
      acceptedQuoteId={job.acceptedQuoteId}
      canDraft={Boolean(job.inquiryId)}
      onDuplicated={(copy) => {
        void invalidateJob()
        navigate({ to: '/pipeline/$id/quotes/$quoteId', params: { id: jobId, quoteId: copy.id } })
      }}
    />
  )

  const quotesCard = (
    <JobQuotes
      key={`quotes-${layoutPhase}`}
      jobId={jobId}
      quoteIds={job.quoteIds}
      acceptedQuoteId={job.acceptedQuoteId}
      inquiry={inquiry}
      onDraftQuote={isLeadPhase && job.inquiryId ? draftQuote : undefined}
      newQuoteAction={newQuoteMenu}
      isBooked={isBookedOrComplete}
    />
  )

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
      {/* Header: the client leads (name, company, copyable contact), and the
          contact line ends with the job's status, the lead's event type and its tags. */}
      <div className="flex flex-wrap items-stretch justify-between gap-4">
        <div className="min-w-0">
          <JobContactHeader
            job={job}
            tags={
              (hasStatus || inquiry) && (
                <div className="flex flex-wrap items-center gap-1.5">
                  {hasStatus && (
                    <Badge variant={jobDetailStatusVariant(job.status)}>{formatEnumName(job.status, JobStatusEnum)}</Badge>
                  )}
                  <EventTypeBadge type={inquiry?.eventTypeKey} />
                  {inquiry && <InquiryTagsEditor inquiry={inquiry} />}
                </div>
              )
            }
          />
        </div>
        {/* Buttons on top, assignee pinned to the bottom: the header row stretches
            both columns to the taller one's height and justify-between keeps the
            buttons level with the client's name and the assignee at the bottom,
            beside the contact and badges line. */}
        <div className="flex flex-col items-end justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!job.inquiryId || isCopyingPortalLink}
              title={job.inquiryId ? undefined : 'This job has no linked inquiry'}
              onClick={copyGenericLink}
            >
              <Share2 className="h-4 w-4" />
              {isCopyingPortalLink ? 'Generating…' : 'Portal link'}
            </Button>
            {/* A lead with a draft quote opens it from here; starting a quote
                is the Quotes card's "+ New quote". Once booked, the action
                follows the money and the next event. */}
            {primaryQuoteAction.kind === 'open-draft' && (
              <Button size="sm" variant="brand" asChild className="shrink-0">
                <Link to="/pipeline/$id/quotes/$quoteId" params={{ id: jobId, quoteId: primaryQuoteAction.quoteId }}>
                  <SquarePen className="h-4 w-4" />
                  Open draft quote
                </Link>
              </Button>
            )}
            <JobBookingCta job={job} action={bookingAction} />
            <JobActionsMenu job={job} inquiry={inquiry} />
          </div>
          <JobAssignee job={job} />
        </div>
      </div>

      {isDeclined && inquiry && <RejectReasonBanner inquiry={inquiry} />}
      {inquiry && <LeadReferralStatus organizationId={job.organizationId} inquiryId={inquiry.id} />}

      {/* Lead / Quote / Booking strip, one line per step; viewing an earlier step hangs a banner under it. */}
      <div>
        <div
          aria-label="Job progress"
          className={cn(
            'grid grid-cols-1 divide-y rounded-xl border bg-card p-1.5 lg:grid-cols-3 lg:divide-x lg:divide-y-0',
            isViewingOtherPhase && 'rounded-b-none border-status-warning-border'
          )}
        >
          {stageCells.map(({ label, title, subtitle }, index) => {
            const leadStageInquiryId = index === 0 && isLeadPhase ? job.inquiryId : undefined
            return (
              <PipelineStageCell
                key={label}
                variant="compact"
                index={index}
                label={label}
                state={phaseSegments[index]}
                onSelect={
                  canViewPhases && phaseSegments[index] !== 'future'
                    ? () => viewPhase(label)
                    : undefined
                }
                selected={canViewPhases && label === (viewStep ?? liveStep)}
                title={title}
                subtitle={subtitle}
                control={
                  leadStageInquiryId && (
                    // Renders its own status dot (animated dot → loader → new-stage dot
                    // while the move is in flight) since it owns the pending state.
                    <PipelineLeadStageControl
                      inquiryId={leadStageInquiryId}
                      quoteStatuses={quoteStatuses}
                      className="min-w-0 shrink-0"
                    />
                  )
                }
              />
            )
          })}
        </div>
        {isViewingOtherPhase && viewStep && (
          <JobPhaseViewBanner viewStep={viewStep} liveStep={liveStep} onBack={() => viewPhase(undefined)} />
        )}
      </div>

      {showConflicts && (
        <LeadOverlapStrip
          overlappingInquiries={overlaps.overlappingInquiries}
          overlappingEvents={overlaps.overlappingEvents}
        />
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="flex min-w-0 flex-col gap-4">
          {isComplete && (
            <JobWrapUp
              job={job}
              events={events}
              invoices={invoicesQuery.invoices}
              isInvoicesLoading={invoicesQuery.isLoading}
            />
          )}

          {job.inquiryId && (
            <JobOriginalInquiry
              // Remounts per laid-out phase so its default collapse applies again.
              key={`inquiry-${layoutPhase}`}
              inquiry={inquiry}
              isLoading={isInquiryLoading}
              // A new quote from the event type's template, the New quote menu's default.
              onDraftQuote={!jobDetailHasQuote(job) ? draftQuote : undefined}
              draftQuoteId={leadDraftQuote?.id}
              defaultCollapsed={isRequestCollapsed}
              jobId={job.id}
              plain
            />
          )}

          <JobEvents
            key={`events-${layoutPhase}`}
            jobId={jobId}
            inquiryId={job.inquiryId}
            events={events}
            isLoading={eventsQuery.isLoading}
            inquiry={inquiry}
            isInquiryLoading={isInquiryLoading}
            defaultCollapsed={isEventsCollapsed}
            readFirst={isQuotingPhase}
            quoteId={currentQuote?.id}
          />

          {/* Every phase reads the events before the quotes built from them. */}
          {quotesCard}

          {/* Booked: one Booking card, the invoices beside the logistics (a
              complete job has no logistics left, so its invoices take the width). */}
          {showBookingCard && (
            <Card aria-label="Booking">
              <div className={cn(showLogistics && 'grid gap-6 max-lg:divide-y lg:grid-cols-2 lg:divide-x')}>
                <JobBooking
                  job={job}
                  invoices={invoicesQuery.invoices}
                  isInvoicesLoading={invoicesQuery.isLoading}
                  isInvoicesError={invoicesQuery.isError}
                  embedded
                />
                {showLogistics && (
                  <JobLogisticsCard
                    job={job}
                    events={events}
                    isEventsLoading={eventsQuery.isLoading}
                    openCheck={openCheck}
                    onOpenCheck={openLogisticsCheck}
                    embedded
                  />
                )}
              </div>
            </Card>
          )}

          {/* The lead's emails, files and workflow runs. */}
          <JobActivityCard jobId={job.id} inquiryId={job.inquiryId} contactId={job.contactId} />
        </div>

        {/* rail */}
        <div className="flex min-w-0 flex-col gap-4">
          <JobRail job={job} stage="job" eventDay={jobEarliestEventDay(events)} />
        </div>
      </div>
    </div>
  )
}
